import { Component, Inject, OnInit } from '@angular/core';
import { DeliveryNoteBundleWithOrgSiteKey } from '@app/consignor/services/consignor.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { DeliveryNotesService } from '../../../../api/services/delivery-notes.service';
import { ErrorHandler } from '@app/core/services/error-handler.service';
import { lastValueFrom, Observable } from 'rxjs';
import { BinaryFileType } from '../../../../api/models/binary-file-type';
import { DeliveryNoteBundleOverview } from '../../../../api/models/delivery-note-bundle-overview';

@Component({
    selector: 'app-view-bundle-dialog',
    templateUrl: './view-bundle-dialog.component.html',
    styleUrls: ['./view-bundle-dialog.component.scss'],
    standalone: false,
})
export class ViewBundleDialogComponent implements OnInit {
    deliveryNoteBundle:
        | DeliveryNoteBundleWithOrgSiteKey
        | DeliveryNoteBundleOverview;
    downloadedPdfs: Blob[] = [];
    currentlyViewedPdf?: Blob;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        private data: {
            deliveryNoteBundle:
                | DeliveryNoteBundleWithOrgSiteKey
                | DeliveryNoteBundleOverview;
            organizationSiteKey: string;
            context: 'consignor' | 'carrier' | 'consignee';
        },
        private translateService: TranslateService,
        private spinner: NgxSpinnerService,
        private deliveryNotesService: DeliveryNotesService,
        private errorHandler: ErrorHandler
    ) {}

    ngOnInit(): void {
        this.deliveryNoteBundle = this.data.deliveryNoteBundle;
        if (this.deliveryNoteBundle.numberOfDeliveryNotes > 0) {
            this.loadBundle();
        }
    }

    async loadBundle(): Promise<void> {
        try {
            const blobData = await lastValueFrom(
                this.getBlobObservableForContext(this.data.context)!
            );

            this.currentlyViewedPdf = blobData;
        } catch (error) {
            this.translateService.instant('GLOBAL.ERROR.UNSPECIFIED');
        }
    }

    async downloadDeliveryBundle(): Promise<void> {
        try {
            this.spinner.show();
            const url = window.URL.createObjectURL(this.currentlyViewedPdf!);
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.href = url;
            const bundleNumber = this.data.deliveryNoteBundle.bundleNumber;
            a.download = bundleNumber.replace('.', '-') + '.pdf';
            a.click();
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    getBlobObservableForContext(
        context: string
    ): Observable<BinaryFileType> | undefined {
        switch (context) {
            case 'consignor':
                return this.deliveryNotesService.getDeliveryNotePdfsForBundle({
                    organizationSiteKey: this.data.organizationSiteKey,
                    deliveryNoteBundleKey: this.data.deliveryNoteBundle._key,
                });
            case 'carrier':
                return this.deliveryNotesService.getCarrierDeliveryNotePdfsForBundle(
                    {
                        organizationSiteKey: this.data.organizationSiteKey,
                        deliveryNoteBundleKey:
                            this.data.deliveryNoteBundle._key,
                    }
                );
            case 'consignee':
                return this.deliveryNotesService.getConsigneeDeliveryNotePdfsForBundle(
                    {
                        organizationSiteKey: this.data.organizationSiteKey,
                        deliveryNoteBundleKey:
                            this.data.deliveryNoteBundle._key,
                    }
                );
        }

        return undefined;
    }
}
